/**
 * Balíkovna pickup-point widget.
 *
 * The carrier ships no script and no global — the caller's page embeds an
 * iframe pointed at the carrier's map, and the map answers over
 * `window.postMessage`. Unlike Slovak Post, its messages are a *structured
 * object*, not a JSON string, so every frame is decoded through a schema
 * directly against `event.data`.
 */
import { Context, Data, Schema } from 'effect';
import type { Effect } from 'effect';

import { ErrorTag } from '../../data/error';
import { ContextTag } from '../context';
import { PickupCarrier, type PickupPoint } from '../shipping/PickupPoint';
import { decodePlacementId } from '../shipping/PlacementId';

/** The carrier's pickup-point map. No API key: any embedding site can load it. */
export const BALIKOVNA_IFRAME_URL =
  'https://b2c.cpost.cz/locations/?type=BALIKOVNY&skipLocation=false';

export type BalikovnaT = {
  readonly id: string;
  readonly name: string;
  readonly address: string;
  readonly zip: string;
};

/**
 * Balíkovna routes parcels by postcode. The backend therefore stores the postcode.
 * The carrier does not report a city.
 *
 * The implementation manual requires ZIP and NAME for an address label.
 * It requires the outlet postcode in ZIP. The `id` field is not label input.
 * The example payload represents `id` as a bare number (`"id": 123`).
 * The recommended address combines ZIP and NAME as "BALÍKOVNA / 160 00 Praha 6".
 *
 * Sending `id` puts a non-postcode where the postcode belongs.
 * The backend's syntactic rule cannot distinguish a five-digit outlet id.
 * The browser test uses different `id` and `zip` values to pin this mapping.
 *
 * @see https://www.balikovna.cz/documents/20124/8280365/BAL_2024_manual+implementace_Vyhledavani_EN.pdf
 */
export const toPickupPoint = (point: BalikovnaT): PickupPoint => ({
  carrier: PickupCarrier.Balikovna,
  placementId: decodePlacementId(point.zip),
  name: point.name,
  street: point.address,
  zip: point.zip,
});

/**
 * Enough of a frame to tell "the carrier chose a point" from every other
 * message the page receives. A frame that fails this is not the carrier
 * talking, and is dropped without comment.
 */
export const BalikovnaMessageSchema = Schema.Struct({
  message: Schema.Literal('pickerResult'),
});

/**
 * The point itself. A frame that passes the envelope and fails this is a
 * protocol breach.
 *
 * The carrier's implementation manual documents `id` as a number and lists
 * only `zip` and `name` as parameters a label needs; `address` sits among the
 * optional ones. The schema accepts both `id` shapes and requires only `zip`,
 * because `zip` is what the backend stores.
 */
export const BalikovnaSelectionSchema = Schema.Struct({
  message: Schema.Literal('pickerResult'),
  point: Schema.Struct({
    id: Schema.Union([Schema.String, Schema.Finite]),
    address: Schema.optional(Schema.String),
    zip: Schema.NonEmptyString,
    name: Schema.optional(Schema.String),
  }),
});

export type BalikovnaSelection = Schema.Schema.Type<typeof BalikovnaSelectionSchema>;

/** The carrier omits `name` and `address` for some points; the picker still needs a label. */
export const toBalikovnaT = (selection: BalikovnaSelection): BalikovnaT => ({
  id: String(selection.point.id),
  name: selection.point.name ?? '',
  address: selection.point.address ?? '',
  zip: selection.point.zip,
});

export class BalikovnaWidgetNotLoadedError extends Data.TaggedError(
  ErrorTag.BalikovnaWidgetNotLoadedError
)<{
  message: string;
}> {}

/**
 * The iframe sends no cancel frame of its own — the shopper cancels by
 * closing the dialog that hosts it, which interrupts the fiber running
 * `pick`. This error is for the flow layer to map that interruption onto,
 * not for the Live layer to raise.
 */
export class BalikovnaWidgetCancelledError extends Data.TaggedError(
  ErrorTag.BalikovnaWidgetCancelledError
)<{
  message: string;
}> {}

export class BalikovnaWidgetPayloadError extends Data.TaggedError(
  ErrorTag.BalikovnaWidgetPayloadError
)<{
  message: string;
  cause?: unknown;
}> {}

export type BalikovnaWidgetError =
  | BalikovnaWidgetNotLoadedError
  | BalikovnaWidgetCancelledError
  | BalikovnaWidgetPayloadError;

export type BalikovnaWidgetOptions = {
  /** CSS selector of the element the iframe is appended into. It must already be in the document. */
  readonly embedSelector: string;
};

export type BalikovnaWidgetService = {
  readonly pick: (
    options: BalikovnaWidgetOptions
  ) => Effect.Effect<BalikovnaT, BalikovnaWidgetError>;
  readonly close: Effect.Effect<void>;
};

export class BalikovnaWidget extends Context.Service<BalikovnaWidget, BalikovnaWidgetService>()(
  ContextTag.BalikovnaWidget
) {}
